import React from "react"
import {Box, Card, Avatar, IconButton, Button, Chip, TextField, Typography} from "@mui/material"
import {useFormContext, useWatch} from "react-hook-form"
import CloseIcon from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"

import {OfferFormDataV2, discountRateSchema, validateOneOffAgainstTiers} from "./offerFormValidation"
import {OneOffIcon} from "./OneOffIcon"
import {DiscountSummary} from "./DiscountSummary"

export function OneOffSettlementCard({onDelete, balanceInCents}: {onDelete: () => void; balanceInCents: number}) {
	const {
		control,
		setValue,
		trigger,
		clearErrors,
		formState: {errors},
	} = useFormContext<OfferFormDataV2>()
	const watchOneOff = useWatch({control, name: "oneOff"})
	const watchPaymentPlanTiers = useWatch({control, name: "paymentPlan.tiers"}) ?? []

	const editing = watchOneOff?.isEditing === true
	const [tempValue, setTempValue] = React.useState<number | "">(watchOneOff?.discountRate || "")
	const [localError, setLocalError] = React.useState<string | null>(null)

	const formError = errors.oneOff?.discountRate?.message
	const error = localError || (!editing && formError) || null

	const startEditing = () => {
		if (watchOneOff) {
			setValue("oneOff", {...watchOneOff, isEditing: true})
		}
	}

	const stopEditing = () => {
		if (watchOneOff) {
			setValue("oneOff", {...watchOneOff, isEditing: false})
		}
	}

	const handleDelete = () => {
		setValue("oneOff", undefined)
		onDelete()
	}

	if (!watchOneOff) {
		return null
	}

	if (!editing) {
		return (
			<Card sx={{backgroundColor: "#CAE8FA", border: "1px solid #66BDEE", borderRadius: "0.25rem"}} elevation={0}>
				<Box sx={{display: "flex", alignItems: "center", p: "1rem", gap: "1rem"}}>
					<Avatar sx={{backgroundColor: "#66BDEE", width: "2rem", height: "2rem"}}>
						<OneOffIcon sx={{fontSize: "1.25rem", color: "#000244"}} />
					</Avatar>
					<Box sx={{flex: 1, display: "flex", flexDirection: "column", gap: "0.5rem"}}>
						<Box sx={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
							<Typography sx={{fontSize: "0.875rem", fontWeight: 500, color: "rgba(0,0,0,0.87)"}}>
								One-off settlement
							</Typography>
							<Chip
								label={`${watchOneOff?.discountRate}% discount`}
								size="small"
								sx={{
									backgroundColor: "white",
									border: "1px solid rgba(0,0,0,0.23)",
									fontSize: "0.8125rem",
								}}
							/>
							{error && <Chip label={error} size="small" color="error" />}
						</Box>
						<Typography sx={{fontSize: "0.75rem", color: "rgba(0,0,0,0.6)"}}>
							Customer settles the balance with a single payment.
						</Typography>
					</Box>
					<Box display="flex" alignItems="center">
						<IconButton size="small" onClick={startEditing} sx={{color: "rgba(0,0,0,0.54)"}}>
							<EditIcon sx={{fontSize: "1rem"}} />
						</IconButton>
						<IconButton size="small" onClick={handleDelete} sx={{color: "rgba(0,0,0,0.54)"}}>
							<CloseIcon sx={{fontSize: "1.5rem"}} />
						</IconButton>
					</Box>
				</Box>
			</Card>
		)
	}

	const handleConfirm = async () => {
		const result = discountRateSchema.safeParse(tempValue === "" ? undefined : tempValue)
		if (!result.success) {
			setLocalError(result.error.issues[0]?.message || "Invalid discount rate")
			return
		}

		const crossValidationError = validateOneOffAgainstTiers(result.data, watchPaymentPlanTiers)
		if (crossValidationError) {
			setLocalError(crossValidationError)
			return
		}

		setLocalError(null)
		setValue("oneOff", {discountRate: result.data, confirmed: true, isEditing: false})
		clearErrors("offerTypes")
		clearErrors("oneOff")
		await trigger("oneOff")
	}

	const handleCancel = () => {
		if (watchOneOff?.confirmed) {
			setTempValue(watchOneOff.discountRate)
			setLocalError(null)
			stopEditing()
			return
		}
		onDelete()
	}

	return (
		<Box>
			<Card
				sx={{
					backgroundColor: "#CAE8FA",
					border: "1px solid #66BDEE",
					borderBottom: "none",
					borderRadius: "0.25rem 0.25rem 0 0",
				}}
				elevation={0}
			>
				<Box sx={{display: "flex", alignItems: "center", p: "1rem", gap: "1rem"}}>
					<Avatar sx={{backgroundColor: "#66BDEE", width: "2rem", height: "2rem"}}>
						<OneOffIcon sx={{fontSize: "1.25rem", color: "#000244"}} />
					</Avatar>
					<Box sx={{flex: 1}}>
						<Typography sx={{fontSize: "0.875rem", fontWeight: 500, color: "rgba(0,0,0,0.87)"}}>
							One-off settlement
						</Typography>
						<Typography sx={{fontSize: "0.75rem", color: "rgba(0,0,0,0.6)"}}>
							Customer settles the balance with a single payment.
						</Typography>
					</Box>
					<IconButton size="small" onClick={handleDelete} sx={{color: "rgba(0,0,0,0.54)"}}>
						<CloseIcon />
					</IconButton>
				</Box>
			</Card>
			<Box
				sx={{
					backgroundColor: "#E5F3FD",
					border: "1px solid #66BDEE",
					borderTop: "none",
					borderRadius: "0 0 0.25rem 0.25rem",
					p: "1rem",
					display: "flex",
					flexDirection: "column",
					gap: "1rem",
				}}
			>
				<TextField
					label="Discount %"
					type="number"
					fullWidth
					value={tempValue}
					onChange={(e) => {
						setTempValue(e.target.value === "" ? "" : Number(e.target.value))
						setLocalError(null)
					}}
					error={!!error}
					helperText={error}
					slotProps={{
						input: {sx: {backgroundColor: "white"}},
						htmlInput: {min: 0, max: 100, step: 1},
					}}
				/>
				<DiscountSummary balanceInCents={balanceInCents} discountRate={tempValue} />
				<Box display="flex" gap="0.5rem" justifyContent="flex-end">
					<Button
						variant="outlined"
						size="small"
						onClick={handleCancel}
						sx={{
							textTransform: "uppercase",
							borderColor: "#474CDE",
							color: "#474CDE",
							backgroundColor: "white",
							"&:hover": {borderColor: "#3539b5", backgroundColor: "rgba(71, 76, 222, 0.04)"},
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						size="small"
						onClick={handleConfirm}
						sx={{
							textTransform: "uppercase",
							backgroundColor: "#474CDE",
							"&:hover": {backgroundColor: "#3539b5"},
						}}
					>
						Confirm
					</Button>
				</Box>
			</Box>
		</Box>
	)
}
